import * as colorPicker from '@zag-js/color-picker';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import './ColorPicker.styles.css';
import { ColorInput } from './ColorInput';
import { parseColorPickerColor, parseWebColor } from './colorPicker.utils';
import { ColorPickerPopup } from './ColorPickerPopup';
import type { ColorFormat, WebColor } from './ColorPicker';

interface ColorPickerFieldProps {
  adornment?: ReactNode;
  color: WebColor;
  disabled?: boolean;
  fullWidth?: boolean;
  offset?: number;
  onChange: (color: WebColor) => void;
  onChangeEnd: (color: WebColor) => void;
  onOpenChange?: (isOpen: boolean) => void;
  showAlpha?: boolean;
  showColorEditor?: boolean;
  size?: 'small' | 'large';
}

export function ColorPickerField({
  adornment,
  color,
  disabled = false,
  fullWidth = false,
  offset = 0,
  onChange,
  onChangeEnd,
  onOpenChange,
  showAlpha = true,
  showColorEditor = true,
  size = 'small',
}: ColorPickerFieldProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<ColorFormat>('HEX');
  const triggerRef = useRef<HTMLElement>(null);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (isFplMigrationEnabled || !open) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (target.closest('[data-color-picker-popup]')) return;
      handleOpenChange(false);
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isFplMigrationEnabled, open, handleOpenChange]);

  const handleColorPickerChange = useCallback(
    (color: colorPicker.Color) => {
      onChange(parseColorPickerColor(color));
    },
    [onChange],
  );

  const handleColorPickerChangeEnd = useCallback(
    (color: colorPicker.Color) => {
      onChangeEnd(parseColorPickerColor(color));
    },
    [onChangeEnd],
  );

  const onColorInputChange = useCallback(
    (color: colorPicker.Color) => {
      handleColorPickerChangeEnd(color);
    },
    [handleColorPickerChangeEnd],
  );

  const onColorBoxClick = useCallback(() => {
    if (disabled) return;
    handleOpenChange(!open);
  }, [disabled, open, handleOpenChange]);

  const onPickerClosed = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const colorPickerValue = parseWebColor(color).toFormat('rgba');
  return (
    <>
      <ColorInput
        adornment={adornment}
        color={colorPickerValue}
        disabled={disabled}
        format={format}
        fullWidth={fullWidth}
        innerRef={triggerRef}
        isOpen={open}
        onChange={onColorInputChange}
        onChangeOngoing={handleColorPickerChange}
        onColorBoxClick={onColorBoxClick}
        showAlpha={showAlpha}
        showColorEditor={showColorEditor}
        size={size}
        withColorBox
      />
      <ColorPickerPopup
        color={colorPickerValue}
        format={format}
        offset={offset}
        onChange={handleColorPickerChange}
        onChangeEnd={handleColorPickerChangeEnd}
        onClose={onPickerClosed}
        onFormatChange={setFormat}
        isOpen={!disabled && open}
        showAlpha={showAlpha}
        triggerRef={triggerRef}
      />
    </>
  );
}
